import { useCallback, useRef, useState } from 'react';

import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '@src/components/ui';
import { useSidebar } from '@src/context/hooks';
import { useClickOutside } from '@src/corelib/hooks';

import { roleLabel } from './roles';


function OrgAvatar({ name, className }) {
  return (
    <span
      className={cn(
        'bg-brand-gradient grid size-6 shrink-0 place-items-center rounded-md',
        'text-[10px] font-bold text-white',
        className,
      )}
    >
      {(name || '—').charAt(0).toUpperCase()}
    </span>
  );
}

OrgAvatar.displayName = 'OrgAvatar';


/** A single org row in the open dropdown: avatar + name + role, a check on the
 * active one. */
function OrgOption({ membership, active, onSelect }) {
  const orgName = membership.seller?.name || '—';

  return (
    <button
      type="button"
      className={cn(
        'flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left',
        'hover:bg-surface-2',
      )}
      onClick={onSelect}
    >
      <OrgAvatar name={orgName} />

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-table font-semibold text-ink">{orgName}</span>
        <span className="text-[10.5px] text-ink-subtle">{roleLabel(membership.role)}</span>
      </span>

      {active ? <Check className="size-4 shrink-0 text-primary" /> : null}
    </button>
  );
}

OrgOption.displayName = 'OrgOption';


/** Org switcher per the .pen Sidebar component (#213). With a single membership
 * it renders the static org block (no chevron, inert) exactly as before. With
 * several, the closed block stays pixel-identical but becomes a button that
 * opens a dropdown of every membership; picking one calls `onSelect` to switch
 * the active org. Collapsed sidebar keeps the avatar-only treatment. */
function SidebarOrgSwitcher({ membership, memberships, onSelect }) {
  const { isCollapsed } = useSidebar();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useClickOutside(ref, useCallback(() => setOpen(false), []), open);

  if (!membership) {
    return null;
  }

  const orgName = membership.seller?.name || '—';
  // Auto-hide the switching affordance for single-org users (and when collapsed,
  // where the design shows only the avatar): the block is inert, no chevron.
  const switchable = (memberships?.length || 0) > 1 && !isCollapsed;

  const closedBlock = (
    <span
      className={cn(
        'flex h-[46px] w-full items-center justify-between rounded-lg border border-hairline',
        'bg-surface-2 px-2.5',
        isCollapsed && 'justify-center border-0 bg-transparent px-0',
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <OrgAvatar name={orgName} />

        {!isCollapsed
          ? (
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-table font-semibold text-ink">{orgName}</span>
              <span className="text-[10.5px] text-ink-subtle">{roleLabel(membership.role)}</span>
            </span>
          )
          : null}
      </span>

      {!isCollapsed ? <ChevronsUpDown className="size-[15px] shrink-0 text-ink-subtle" /> : null}
    </span>
  );

  if (!switchable) {
    return (
      <div
        className="flex items-center"
        title={isCollapsed ? orgName : undefined}
      >
        {closedBlock}
      </div>
    );
  }

  const handleSelect = (id) => {
    setOpen(false);
    if (id !== membership.id) {
      onSelect(id);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-label="Switch organization"
        className="block w-full cursor-pointer"
        aria-haspopup="listbox"
        onClick={() => setOpen((value) => !value)}
      >
        {closedBlock}
      </button>

      {open
        ? (
          <div
            aria-label="Organizations"
            className={cn(
              'absolute left-0 right-0 top-[52px] z-50 flex max-h-[320px] flex-col gap-0.5 overflow-y-auto',
              'rounded-xl border border-hairline bg-surface-1 p-1.5 shadow-2xl',
            )}
            role="listbox"
          >
            {memberships.map((entry) => (
              <OrgOption
                key={entry.id}
                membership={entry}
                active={entry.id === membership.id}
                onSelect={() => handleSelect(entry.id)}
              />
            ))}
          </div>
        )
        : null}
    </div>
  );
}

SidebarOrgSwitcher.displayName = 'SidebarOrgSwitcher';
export default SidebarOrgSwitcher;
